const { debounce } = window.BAO.Utils

class CartItem extends window.BAO.CustomElement() {
  constructor () {
    super()

    this._loading = false
    this.sections = this.inPushCart
      ? window.BAO.sectionRenderer.pushCartSections
      : this.onCartPage
        ? window.BAO.sectionRenderer.cartSections
        : []
  }

  setupListeners () {
    super.setupListeners()

    this.changeLineItem = this.changeLineItem.bind(this)
    this.removeLineItem = this.removeLineItem.bind(this)
    this.onQuantityChange = this.onQuantityChange.bind(this)
    this.debouncedOnQuantityChange = debounce((event) => {
      this.onQuantityChange(event)
    }, 500)

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.debouncedOnQuantityChange,
    )

    if (this.els?.remove?.exists) {
      this.listeners.add(this.els.remove.element, 'click', this.removeLineItem)
    }

    if (this.els?.variant && this.els?.variant.exists) {
      this.listeners.add(
        this.els.variant.element,
        'change',
        this.changeLineItem,
      )
    }
  }

  // for testing
  async getCartInfo () {
    try {
      const response = await fetch('/cart.js')
      const cart = await response.json()

      const cartTotal = cart.total_price
      const cartItems = cart.items

      return { cartTotal, cartItems }
    } catch (error) {
      console.error('Error fetching cart total:', error)
      return { cartTotal: null, cartItems: null }
    }
  }

  async removeLineItem (e, { silent = false } = {}) {
    try {
      e?.preventDefault()

      this.loading = true

      const state = await window.BAO.Cart.removeItem(
        this.dataset.key,
        {
          sections: this.mappedSections,
        },
      )

      console.info({
        __: 'removeLineItem',
        cartInfo: await this.getCartInfo(),
        datasetKey: this.dataset.key,
        state,
      })

      this.loading = false

      if (!silent) {
        if ('sections' in state) {
          this.renderSections(state.sections)
        }
      }

      return state
    } catch (error) {
      console.error('Error in removeLineItem:', {
        message: error.message,
        stack: error.stack,
        datasetKey: this.dataset.key,
      })
      this.loading = false
    }
  }

  async changeLineItem (e) {
    try {
      this.loading = true

      const newVariantId = e.target.value
      const currentQuantity = e?.target?.dataset?.currentQuantity ?? 1
      const maxQuantity = 999
      const quantity = Math.min(currentQuantity, maxQuantity)

      console.info({
        __: 'changeLineItem before adding new variant',
        cartInfo: await this.getCartInfo(),
      })

      // Step 1: Add the new variant first (silently)
      let state = await window.BAO.Cart.addItem(
        { id: newVariantId, quantity },
        {
          sections: this.mappedSections,
          silent: true, // Ensures no UI flicker
        },
      )

      console.info({
        __: 'changeLineItem after adding new variant',
        cartInfo: await this.getCartInfo(),
      })

      // Step 2: Remove the old variant
      state = await this.removeLineItem(e, { silent: false })

      console.info({
        __: 'changeLineItem after removing old variant',
        cartInfo: await this.getCartInfo(),
      })

      return state
    } catch (error) {
      console.error('Error in changeLineItem:', {
        message: error.message,
        stack: error.stack,
        datasetKey: this.dataset.key,
        newVariantId: e.target.value,
      })
    } finally {
      console.info({
        __: 'changeLineItem finally',
        cartInfo: await this.getCartInfo(),
      })

      this.loading = false
    }
  }

  async onQuantityChange (e) {
    try {
      this.loading = true

      const state = await window.BAO.Cart.updateQuantity(
        this.dataset.key,
        e.detail.value,
        { sections: this.mappedSections },
      )

      this.loading = false

      if ('sections' in state) this.renderSections(state.sections)
    } catch (error) {
      console.error('Error in onQuantityChange:', {
        message: error.message,
        stack: error.stack,
        quantity: e.detail.value,
        datasetKey: this.dataset.key,
        value: e.detail.value,
        sections: this.mappedSections,
      })
      this.loading = false
    }
  }

  get mappedSections () {
    return this.sections.map(s => s.section)
  }

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    if (this.inPushCart) {
      this.pushCart.loading = val
    }

    this.setAttribute('aria-busy', val.toString())
  }

  get pushCart () {
    return this.closest('push-cart')
  }

  get inPushCart () {
    return this.pushCart != null
  }

  get cart () {
    return this.closest('cart-items')
  }

  get onCartPage () {
    return this.cart != null
  }
}

if (!customElements.get('cart-item')) {
  customElements.define('cart-item', CartItem)
}
